import React, { useState, useEffect, useRef } from 'react';
import { Input, Spinner, Badge, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { workTypesAPI } from '../../helpers/api';

interface WorkType {
  id: string;
  name: string;
  unit: string;
  description?: string;
  isActive: boolean;
}

interface SiteWork {
  id: string;
  targetQuantity: number | null;
  status: string;
  notes: string | null;
  site: {
    id: string;
    name: string;
    code: string;
    project: {
      id: string;
      name: string;
      code: string;
    };
  };
}

interface WorkTypeMaterial {
  id: string;
  logoItemCode: string;
  logoItemName: string;
  createdAt: string;
}

interface LogoItem {
  code: string;
  name: string;
}

interface WorkTypeDetail extends WorkType {
  siteWorks: SiteWork[];
}

interface WorkTypeDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  workType: WorkType | null;
  onUpdate?: () => void;
}

// Default unit options
const DEFAULT_UNITS = ['m²', 'm³', 'ton', 'kg', 'metre', 'adet', 'litre', 'saat'];

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'NOT_STARTED': return 'Başlamadı';
    case 'IN_PROGRESS': return 'Devam Ediyor';
    case 'COMPLETED': return 'Tamamlandı';
    default: return status;
  }
};

const getStatusStyle = (status: string) => {
  switch (status) {
    case 'NOT_STARTED': return 'bg-secondary bg-opacity-10 text-secondary';
    case 'IN_PROGRESS': return 'bg-warning bg-opacity-10 text-warning';
    case 'COMPLETED': return 'bg-success bg-opacity-10 text-success';
    default: return 'bg-secondary bg-opacity-10 text-secondary';
  }
};

const WorkTypeDetailModal: React.FC<WorkTypeDetailModalProps> = ({
  isOpen,
  toggle,
  workType,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentIsActive, setCurrentIsActive] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    customUnit: '',
    description: '',
  });

  // Full work type details with siteWorks
  const [workTypeDetail, setWorkTypeDetail] = useState<WorkTypeDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Units for dropdown
  const [units, setUnits] = useState<string[]>([]);

  // Material mapping
  const [materials, setMaterials] = useState<WorkTypeMaterial[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [materialSearch, setMaterialSearch] = useState('');
  const [logoItems, setLogoItems] = useState<LogoItem[]>([]);
  const [searchingItems, setSearchingItems] = useState(false);
  const [addingMaterial, setAddingMaterial] = useState(false);
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch full details when workType changes
  useEffect(() => {
    if (workType && isOpen) {
      setFormData({
        name: workType.name || '',
        unit: workType.unit || '',
        customUnit: '',
        description: workType.description || '',
      });
      setCurrentIsActive(workType.isActive);
      setIsEditing(false);
      setShowAddMaterial(false);
      setMaterialSearch('');
      setLogoItems([]);
      fetchWorkTypeDetail(workType.id);
      fetchMaterials(workType.id);
    }
  }, [workType, isOpen]);

  // Update form when units are loaded (to handle custom values)
  useEffect(() => {
    if (workType && units.length > 0) {
      const unitInList = units.includes(workType.unit);
      setFormData(prev => ({
        ...prev,
        unit: unitInList ? workType.unit : '__OTHER__',
        customUnit: unitInList ? '' : workType.unit,
      }));
    }
  }, [workType, units]);

  const fetchWorkTypeDetail = async (id: string) => {
    try {
      setLoadingDetail(true);
      const response = await workTypesAPI.getOne(id);
      setWorkTypeDetail(response.data);
    } catch (error) {
      console.error('Work type detail fetch error:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchMaterials = async (workTypeId: string) => {
    try {
      setLoadingMaterials(true);
      const response = await workTypesAPI.getWorkTypeMaterials(workTypeId);
      setMaterials(response.data);
    } catch (error) {
      console.error('Materials fetch error:', error);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleSearchLogoItems = (search: string) => {
    setMaterialSearch(search);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (search.length < 2) {
      setLogoItems([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      try {
        setSearchingItems(true);
        const response = await workTypesAPI.searchLogoItems(search);
        setLogoItems(response.data);
      } catch (error) {
        console.error('Logo items search error:', error);
      } finally {
        setSearchingItems(false);
      }
    }, 300);
  };

  const handleAddMaterial = async (item: LogoItem) => {
    if (!workType) return;
    try {
      setAddingMaterial(true);
      await workTypesAPI.addMaterial(workType.id, {
        logoItemCode: item.code,
        logoItemName: item.name,
      });
      toast.success(`${item.name} eklendi`);
      fetchMaterials(workType.id);
      setMaterialSearch('');
      setLogoItems([]);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ekleme başarısız');
    } finally {
      setAddingMaterial(false);
    }
  };

  const handleRemoveMaterial = async (material: WorkTypeMaterial) => {
    try {
      await workTypesAPI.removeMaterial(material.id);
      toast.success(`${material.logoItemName} kaldırıldı`);
      if (workType) fetchMaterials(workType.id);
    } catch (error: any) {
      toast.error('Kaldırma başarısız');
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await workTypesAPI.getUnits();
      const dbUnits = response.data as string[];
      const allUnits = [...new Set([...dbUnits, ...DEFAULT_UNITS])].sort();
      setUnits(allUnits);
    } catch (error) {
      console.error('Units fetch error:', error);
      setUnits(DEFAULT_UNITS);
    }
  };

  // Get actual unit value
  const getActualUnit = () => formData.unit === '__OTHER__' ? formData.customUnit : formData.unit;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!workType) return;

    const actualUnit = getActualUnit();

    if (!formData.name.trim() || !actualUnit.trim()) {
      toast.error('İş adı ve birim zorunludur');
      return;
    }

    try {
      setSaving(true);

      await workTypesAPI.update(workType.id, {
        name: formData.name.trim(),
        unit: actualUnit.trim(),
        description: formData.description.trim() || undefined,
      });
      toast.success('İş türü güncellendi');
      setIsEditing(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (workType && units.length > 0) {
      const unitInList = units.includes(workType.unit);
      setFormData({
        name: workType.name || '',
        unit: unitInList ? workType.unit : '__OTHER__',
        customUnit: unitInList ? '' : workType.unit,
        description: workType.description || '',
      });
    }
    setIsEditing(false);
  };

  const handleToggleStatus = async () => {
    if (!workType) return;

    try {
      const newStatus = !currentIsActive;
      await workTypesAPI.update(workType.id, { isActive: newStatus });
      setCurrentIsActive(newStatus);
      toast.success(newStatus ? 'İş türü aktif yapıldı' : 'İş türü pasif yapıldı');
      onUpdate?.();
    } catch (error: any) {
      toast.error('İşlem başarısız');
    }
  };

  if (!workType) return null;

  // Calculate statistics
  const siteWorks = workTypeDetail?.siteWorks || [];
  const totalSites = siteWorks.length;
  const inProgressCount = siteWorks.filter(sw => sw.status === 'IN_PROGRESS').length;
  const completedCount = siteWorks.filter(sw => sw.status === 'COMPLETED').length;
  const notStartedCount = siteWorks.filter(sw => sw.status === 'NOT_STARTED').length;

  const headerActions = (
    <>
      {!isEditing ? (
        <button
          type="button"
          className="side-modal-expand-btn me-1"
          onClick={() => {
            fetchUnits();
            setIsEditing(true);
          }}
          title="Düzenle"
        >
          <i className="ri-edit-line"></i>
        </button>
      ) : (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleSave}
            disabled={saving}
            title="Kaydet"
            style={{ color: saving ? '#adb5bd' : '#0ab39c' }}
          >
            {saving ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleCancel}
            disabled={saving}
            title="İptal"
          >
            <i className="ri-close-line"></i>
          </button>
        </>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="İş Türü Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-start justify-content-between">
            <div style={{ flex: 1, marginRight: '1rem' }}>
              {isEditing ? (
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-control-lg mb-2"
                  style={{ fontWeight: 600 }}
                  placeholder="İş Adı"
                />
              ) : (
                <h4 className="mb-1" style={{ fontWeight: 600 }}>
                  {workType.name}
                </h4>
              )}
              {isEditing ? (
                <div className="d-flex flex-column gap-2">
                  <Input
                    type="select"
                    value={formData.unit}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        unit: value,
                        customUnit: value === '__OTHER__' ? formData.customUnit : '',
                      });
                    }}
                    bsSize="sm"
                    style={{ width: '150px' }}
                  >
                    <option value="">Birim Seçin</option>
                    {units.map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                    <option value="__OTHER__">Diğer</option>
                  </Input>
                  {formData.unit === '__OTHER__' && (
                    <Input
                      type="text"
                      value={formData.customUnit}
                      onChange={(e) => setFormData({ ...formData, customUnit: e.target.value })}
                      placeholder="Yeni birim"
                      bsSize="sm"
                      style={{ width: '150px' }}
                    />
                  )}
                </div>
              ) : (
                <span
                  className="px-2 py-1 rounded-pill small bg-info bg-opacity-10 text-info"
                  style={{ fontWeight: 500 }}
                >
                  {workType.unit}
                </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <span
                className={`px-2 py-1 rounded-pill small ${
                  currentIsActive
                    ? 'bg-success bg-opacity-10 text-success'
                    : 'bg-secondary bg-opacity-10 text-secondary'
                }`}
                style={{ fontWeight: 500 }}
              >
                {currentIsActive ? 'Aktif' : 'Pasif'}
              </span>
              {!isEditing && (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-muted"
                  onClick={handleToggleStatus}
                  title={currentIsActive ? 'Pasif Yap' : 'Aktif Yap'}
                >
                  <i className="ri-refresh-line"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Details Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
          <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Detaylar
          </div>
          <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
            {/* Açıklama */}
            <div className="d-flex align-items-start justify-content-between px-3 py-2">
              <span className="text-muted">Açıklama</span>
              {isEditing ? (
                <Input
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Açıklama"
                  bsSize="sm"
                  style={{ width: '200px' }}
                  rows={2}
                />
              ) : (
                <span style={{ fontWeight: 500, textAlign: 'right', maxWidth: '200px' }}>
                  {workType.description || '-'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Malzeme Eşleştirme Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-list-unordered me-1"></i>
              Malzeme Eşleştirme
            </span>
            <Button
              color="light"
              size="sm"
              onClick={() => setShowAddMaterial(!showAddMaterial)}
            >
              <i className={`ri-${showAddMaterial ? 'close' : 'add'}-line me-1`}></i>
              {showAddMaterial ? 'Kapat' : 'Malzeme Ekle'}
            </Button>
          </div>

          {/* Malzeme Arama */}
          {showAddMaterial && (
            <div className="mb-3">
              <div className="position-relative">
                <Input
                  type="text"
                  bsSize="sm"
                  placeholder="Logo malzeme kodu veya adı ara... (min 2 karakter)"
                  value={materialSearch}
                  onChange={(e) => handleSearchLogoItems(e.target.value)}
                  autoFocus
                />
                {searchingItems && (
                  <Spinner size="sm" color="primary" className="position-absolute" style={{ right: '10px', top: '8px' }} />
                )}
              </div>
              {logoItems.length > 0 && (
                <div className="bg-white border rounded-3 mt-1 overflow-hidden" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {logoItems
                    .filter((item) => !materials.some((m) => m.logoItemCode === item.code))
                    .map((item) => (
                    <div
                      key={item.code}
                      className="px-3 py-2 d-flex justify-content-between align-items-center"
                      style={{ borderBottom: '1px solid #f3f3f9', cursor: 'pointer' }}
                      onClick={() => !addingMaterial && handleAddMaterial(item)}
                    >
                      <div>
                        <span style={{ fontWeight: 500 }}>{item.name}</span>
                        <span className="text-muted small d-block">{item.code}</span>
                      </div>
                      <i className="ri-add-circle-line text-success" style={{ fontSize: '1.2rem' }}></i>
                    </div>
                  ))}
                </div>
              )}
              {materialSearch.length >= 2 && !searchingItems && logoItems.filter((item) => !materials.some((m) => m.logoItemCode === item.code)).length === 0 && (
                <div className="text-center py-2 text-muted small">
                  Sonuç bulunamadı
                </div>
              )}
            </div>
          )}

          {/* Mevcut Malzemeler */}
          {loadingMaterials ? (
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" />
            </div>
          ) : materials.length === 0 ? (
            <div className="text-center py-3 rounded-3" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <i className="ri-box-3-line text-muted" style={{ fontSize: '1.5rem' }}></i>
              <p className="mb-0 mt-1 text-muted small">Henüz malzeme eşleştirmesi yapılmamış</p>
            </div>
          ) : (
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              {materials.map((material, idx) => (
                <div
                  key={material.id}
                  className="px-3 py-2 d-flex justify-content-between align-items-center"
                  style={{ borderBottom: idx < materials.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div>
                    <span style={{ fontWeight: 500 }}>{material.logoItemName}</span>
                    <span className="text-muted small d-block">{material.logoItemCode}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0"
                    onClick={() => handleRemoveMaterial(material)}
                    title="Kaldır"
                  >
                    <i className="ri-close-line" style={{ fontSize: '1.1rem' }}></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage Statistics Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-bar-chart-line me-1"></i>
              Kullanım İstatistikleri
            </span>
            <Badge color="primary">{totalSites} şantiye</Badge>
          </div>

          {loadingDetail ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="primary" />
              <p className="mb-0 mt-2 text-muted small">Yükleniyor...</p>
            </div>
          ) : totalSites === 0 ? (
            <div className="text-center py-4 rounded-3" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <i className="ri-building-line text-muted" style={{ fontSize: '2rem' }}></i>
              <p className="mb-0 mt-2 text-muted">Bu iş türü henüz hiçbir şantiyeye atanmamış</p>
            </div>
          ) : (
            <>
              {/* Stats Summary */}
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <div className="rounded-3 p-2 text-center" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                    <div className="text-warning" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                      {inProgressCount}
                    </div>
                    <div className="text-muted small">Devam Eden</div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="rounded-3 p-2 text-center" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                    <div className="text-success" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                      {completedCount}
                    </div>
                    <div className="text-muted small">Tamamlanan</div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="rounded-3 p-2 text-center" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                    <div className="text-secondary" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                      {notStartedCount}
                    </div>
                    <div className="text-muted small">Başlamadı</div>
                  </div>
                </div>
              </div>

              {/* Site List */}
              <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7', maxHeight: '250px', overflowY: 'auto' }}>
                {siteWorks.map((sw, idx) => (
                  <div
                    key={sw.id}
                    className="px-3 py-2"
                    style={{ borderBottom: idx < siteWorks.length - 1 ? '1px solid #e9ecef' : 'none' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <strong>{sw.site.name}</strong>
                        <small className="text-muted d-block">
                          {sw.site.project.name}
                        </small>
                      </div>
                      <div className="text-end">
                        <span
                          className={`px-2 py-1 rounded-pill small ${getStatusStyle(sw.status)}`}
                          style={{ fontWeight: 500 }}
                        >
                          {getStatusLabel(sw.status)}
                        </span>
                        {sw.targetQuantity && (
                          <small className="text-muted d-block mt-1">
                            Hedef: {sw.targetQuantity.toLocaleString('tr-TR')} {workType.unit}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default WorkTypeDetailModal;
